import { create } from "zustand";

interface DevicePresenceState {
  /**
   * Whether THIS client is the member's phone, and should answer presence
   * questions.
   *
   * Set by the mobile layout, which is the only place the phone experience is
   * mounted — the same place `DistractionTracker` reports as `type: "phone"`.
   * A desktop tab receives the question on the same device topic and must stay
   * silent, or every member with a laptop open would look like they had a phone
   * connected.
   */
  isPhone: boolean;
  /**
   * When asking began, or last resumed.
   *
   * Silence is measured from the last sign of life; before any answer has
   * arrived, that is when the questions started. The most recent QUESTION
   * cannot serve: a new one goes out every ten seconds, so no timeout longer
   * than the interval could ever elapse.
   */
  probingSinceMs: number | null;
  /** When the most recent question was asked, by this client. */
  lastPingAtMs: number | null;
  /** When an answer last arrived. */
  lastPongAtMs: number | null;
  markAsPhone: () => void;
  recordPing: (atMs: number) => void;
  recordPong: (atMs: number) => void;
  /** A new session, or a sign-out: nothing learned before carries over. */
  resetPresence: () => void;
}

export const useDevicePresenceStore = create<DevicePresenceState>((set) => ({
  isPhone: false,
  probingSinceMs: null,
  lastPingAtMs: null,
  lastPongAtMs: null,
  markAsPhone: () => set({ isPhone: true }),
  recordPing: (atMs) =>
    set((state) => ({
      lastPingAtMs: atMs,
      probingSinceMs: state.probingSinceMs ?? atMs,
    })),
  recordPong: (atMs) => set({ lastPongAtMs: atMs }),
  resetPresence: () =>
    set({ probingSinceMs: null, lastPingAtMs: null, lastPongAtMs: null }),
}));
